/**
 * Settings → Signals writes (I8), in the shapes the server's ops use: `settings.signals` and
 * `settings.places` are maps of plain values; `settings.weather` and `settings.signalRules` are plain values.
 */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { ulid } from "ulid";
import { commitMessage } from "../core/actor";
import { C } from "../core/schema";
import type { SignalRule } from "../core/signals";
import type { SignalDef } from "./describe";

export interface Place {
  id: string;
  name: string;
  lat: number;
  lon: number;
  radius: number;
  /** place reminders (I9): open tasks with these contexts are listed here */
  contexts?: string[];
  /** minutes to stay before the reminder (default 2) */
  waitMin?: number;
  /** hours between reminders at this place (default 4) */
  gapHours?: number;
}
export interface WeatherCfg {
  enabled: boolean;
  lat: number | null;
  lon: number | null;
}

const DRY = { signal: "weather.rain_next_3h", eq: false };

/** The weather rules for the outside contexts (I8, S1): dry at any time, dry and light, dry and dark. */
export const OUTSIDE_RULES: (SignalRule & { id: string })[] = [
  { id: "weather.outside", filter: { prop: "context", has: "outside" }, when: DRY },
  {
    id: "weather.outside-day",
    filter: { prop: "context", has: "outside-day" },
    when: { and: [DRY, { signal: "sun.up", eq: true }] },
  },
  {
    id: "weather.outside-night",
    filter: { prop: "context", has: "outside-night" },
    when: { and: [DRY, { signal: "sun.up", eq: false }] },
  },
];
const OUTSIDE_IDS = new Set(OUTSIDE_RULES.map((r) => r.id));

/** The outside rules are on when all of them are there (an older workspace may have only the first). */
export const outsideRulesOn = (rules: { id?: string }[]) =>
  OUTSIDE_RULES.every((r) => rules.some((x) => x.id === r.id));

function map(doc: LoroDoc, key: string): LoroMap {
  const settings = doc.getMap(C.settings);
  const cur = settings.get(key) as unknown;
  if (cur instanceof LoroMap) return cur;
  return settings.setContainer(key, new LoroMap());
}

export function setSignalEnabled(doc: LoroDoc, name: string, enabled: boolean): void {
  const defs = map(doc, "signals");
  const cur = (defs.get(name) as SignalDef | undefined) ?? { type: "text" };
  defs.set(name, { ...cur, enabled });
  doc.commit({ message: commitMessage({ op: "defineSignal" }) });
}

export function readWeather(doc: LoroDoc): WeatherCfg {
  const w = (doc.getMap(C.settings).toJSON() as { weather?: Partial<WeatherCfg> }).weather ?? {};
  return { enabled: w.enabled === true, lat: w.lat ?? null, lon: w.lon ?? null };
}

export function setWeather(doc: LoroDoc, cfg: WeatherCfg): void {
  doc.getMap(C.settings).set("weather", { ...cfg });
  doc.commit({ message: commitMessage({ op: "setSetting", path: "weather" }) });
}

export function readRules(doc: LoroDoc): (SignalRule & { id?: string })[] {
  const r = (doc.getMap(C.settings).toJSON() as { signalRules?: unknown }).signalRules;
  return Array.isArray(r) ? (r as (SignalRule & { id?: string })[]) : [];
}

/** The outside contexts the weather rules refer to: added to the context values when missing (schema v4 adds
 * them to every workspace; this covers values someone deleted). */
function ensureOutsideContexts(doc: LoroDoc): void {
  const def = doc.getMap(C.propDefs).get("context") as LoroMap | undefined;
  if (!def) return;
  const spec = (def.get("spec") ?? {}) as { values?: { id: string; label: string; color: string | null }[] };
  const values = spec.values ?? [];
  const missing = ["outside", "outside-day", "outside-night"].filter(
    (id) => !values.some((v) => v.id === id),
  );
  if (missing.length === 0) return;
  const added = missing.map((id) => ({ id, label: `@${id}`, color: null }));
  def.set("spec", { ...spec, values: [...values, ...added] } as never);
}

export function setOutsideRule(doc: LoroDoc, on: boolean): void {
  if (on) ensureOutsideContexts(doc);
  const rest = readRules(doc).filter((r) => !OUTSIDE_IDS.has(r.id ?? ""));
  doc.getMap(C.settings).set("signalRules", on ? [...rest, ...OUTSIDE_RULES] : rest);
  doc.commit({ message: commitMessage({ op: "setSetting", path: "signalRules" }) });
}

export function readPlaces(doc: LoroDoc): Place[] {
  const p = (doc.getMap(C.settings).toJSON() as { places?: Record<string, Place> }).places ?? {};
  return Object.values(p).sort((a, b) => a.name.localeCompare(b.name));
}

export function savePlace(doc: LoroDoc, place: Omit<Place, "id"> & { id?: string }): string {
  const id = place.id ?? ulid();
  const places = map(doc, "places");
  const cur = (places.get(id) as Place | undefined) ?? {};
  places.set(id, { ...cur, ...place, id, name: place.name.trim() });
  doc.commit({ message: commitMessage({ op: "savePlace" }) });
  return id;
}

export function deletePlace(doc: LoroDoc, id: string): void {
  map(doc, "places").delete(id);
  // and out of the place lists that held it, as the server's `delete_place` does
  const lists = map(doc, "placeLists");
  for (const [lid, pl] of Object.entries(lists.toJSON() as Record<string, PlaceList>)) {
    if (pl?.placeIds?.includes(id)) lists.set(lid, { ...pl, placeIds: pl.placeIds.filter((x) => x !== id) });
  }
  doc.commit({ message: commitMessage({ op: "deletePlace" }) });
}

/**
 * Several places under one name (docs/plans/buttons-and-place-lists.md, part 3): a task at the list is reminded at
 * any of them. `settings.placeLists[id] = {id, name, placeIds}`, as the server's `save_place_list` writes it.
 */
export interface PlaceList {
  id: string;
  name: string;
  placeIds: string[];
}

export function readPlaceLists(doc: LoroDoc): PlaceList[] {
  const p = (doc.getMap(C.settings).toJSON() as { placeLists?: Record<string, PlaceList> }).placeLists ?? {};
  return Object.values(p)
    .filter((x) => x && typeof x === "object")
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function savePlaceList(doc: LoroDoc, list: Omit<PlaceList, "id"> & { id?: string }): string {
  const id = list.id ?? ulid();
  map(doc, "placeLists").set(id, { id, name: list.name.trim(), placeIds: [...new Set(list.placeIds)] });
  doc.commit({ message: commitMessage({ op: "savePlaceList" }) });
  return id;
}

export function deletePlaceList(doc: LoroDoc, id: string): void {
  map(doc, "placeLists").delete(id);
  doc.commit({ message: commitMessage({ op: "deletePlaceList" }) });
}
